import React, { useContext } from "react";
import { Box, Grid, IconButton, Modal, SvgIcon } from "@mui/material";
import AppContext from "../../../../AppContext";
import {
  HelpIconInterface,
  Microcopy,
  MicrocopyResource,
} from "../../../../utilities/contentful";
import QuoteModal from "./QuoteModal";
import SaveButton from "./SaveButton";
import ResumeButton from "./ResumeButton";
import { FormikErrors, FormikProps } from "formik";
import {
  PrimaryInsured,
  QuoteRequest,
} from "../../../../components/quote/QuoteRequestData";
import ResetButton from "./ResetButton";
import { FullQuoteResult } from "../../../../components/quote/QuoteData.interface";
import dynamic from "next/dynamic";

import {
  updateCoverageAreaText,
  updateDateText,
  updatePeopleTab,
  updateTripProtectionText,
} from "../../../../components/quote/Form/QuoteHelpers";
import { PurchaseDetails } from "../../../../components/purchase/PurchaseData.interface";
import Quotebox from "./Quotebox";

const SpreadsheetImportModal = dynamic(
  () => import("../../../../components/quote/Form/SpreadsheetImportModal")
);

interface Props {
  microcopy: MicrocopyResource[];
  formikProps: FormikProps<PurchaseDetails>;
  quoteBoxDetails: {
    coverageDuration: string;
    coverageArea: string;
    tripProtection: string;
    numPeople: string;
  };
  restoredQuote: QuoteRequest | null;
  setQuoteBoxDetails: React.Dispatch<
    React.SetStateAction<{
      coverageDuration: string;
      coverageArea: string;
      tripProtection: string;
      numPeople: string;
    }>
  >;
  setQuoteData: React.Dispatch<
    React.SetStateAction<Partial<FullQuoteResult> | null>
  >;
  helpIcons: HelpIconInterface[];
  agentNum: string;
  affiliate?: string;
}

const Quotebar = ({
  microcopy,
  formikProps,
  quoteBoxDetails,
  restoredQuote,
  setQuoteBoxDetails,
  setQuoteData,
  helpIcons,
  agentNum,
  affiliate,
}: Props): JSX.Element => {
  const micro = new Microcopy();
  const appState: any = useContext(AppContext);

  const [startDateTabText, setStartDateTabText] = React.useState({
    startDateText: updateDateText(
      formikProps.values.startDate,
      formikProps.errors.startDate,
      "Start"
    ),
    datesValid: !formikProps.errors.startDate,
  });

  const [endDateTabText, setEndDateTabText] = React.useState({
    endDateText: updateDateText(
      formikProps.values.endDate,
      formikProps.errors.endDate,
      "End"
    ),
    datesValid: !formikProps.errors.endDate,
  });

  const [areaTabText, setAreaTabText] = React.useState({
    coverageAreaText: updateCoverageAreaText(
      formikProps.values.usaTrip,
      formikProps.values.countryObject
    ),
    coverageValid:
      !formikProps.errors.usaTrip && !formikProps.errors.countryObject,
  });

  const [protectionTabText, setProtectionTabText] = React.useState({
    tripProtectionText: updateTripProtectionText(
      formikProps.values.tripProtection
    ),
    protectionValid:
      !formikProps.errors.tripProtection &&
      !formikProps.errors.useOverallTripCost &&
      !formikProps.errors.initialPaymentDate,
  });

  const [peopleTabText, setPeopleTabText] = React.useState({
    peopleText: updatePeopleTab(
      formikProps.values.primaryInsured,
      formikProps.errors.primaryInsured as FormikErrors<
        Partial<PrimaryInsured>
      >[]
    ),
    peopleValid: formikProps.errors.primaryInsured === undefined,
  });

  const handleOpen = (selectedTab: number) => {
    appState.setAppState({
      ...appState.state,
      quoteModalOpen: true,
      selectedTab: selectedTab,
    });
  };

  const handleClose = () => {
    appState.setAppState({
      ...appState.state,
      quoteModalOpen: false,
    });
  };

  React.useEffect(() => {
    if (restoredQuote) {
      formikProps.setValues(restoredQuote);

      appState.setAppState({
        ...appState.state,
        quoteRequestData: restoredQuote,
      });

      window.localStorage.setItem("quote-form", JSON.stringify(restoredQuote));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (
      appState.state.initialLoadFromLocal &&
      appState.state.quoteRequestData &&
      !restoredQuote
    ) {
      formikProps.submitForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appState.state.initialLoadFromLocal, appState.state.quoteRequestData]);

  return (
    <>
      <Grid
        container
        display={"flex"}
        alignItems={"center"}
        minHeight={"50px"}
        flexWrap="nowrap"
        sx={{ width: "100%" }}
      >
        <Grid item sx={{ display: { xs: "none", md: "flex", flexGrow: 1 } }}>
          <Quotebox
            onClick={() => handleOpen(0)}
            text={micro.getFromKey("heading.coverage-duration", microcopy)}
            details={quoteBoxDetails.coverageDuration}
          >
            <SvgIcon sx={{ fontSize: "18px" }}>
              <svg
                viewBox="0 0 18 18"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M6 1.5V4.5"
                  stroke="#1878D0"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M12 1.5V4.5"
                  stroke="#1878D0"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M14.25 3H3.75C2.92157 3 2.25 3.67157 2.25 4.5V15C2.25 15.8284 2.92157 16.5 3.75 16.5H14.25C15.0784 16.5 15.75 15.8284 15.75 15V4.5C15.75 3.67157 15.0784 3 14.25 3Z"
                  stroke="#1878D0"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M2.25 7.5H15.75"
                  stroke="#1878D0"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </SvgIcon>
          </Quotebox>

          <Quotebox
              onClick={() => handleOpen(1)}
              text={micro.getFromKey("heading.coverage-area", microcopy)}
              details={quoteBoxDetails.coverageArea}
              sx={{ display: { md: "none", lg: "block" } }}
          >
            <SvgIcon sx={{ fontSize: "18px" }}>
              <svg
                  viewBox="0 0 17 17"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
              >
                <path
                    d="M1.25 8.25L15.5 1.5L8.75 15.75L7.25 9.75L1.25 8.25Z"
                    stroke="#1878D0"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                />
              </svg>
            </SvgIcon>
          </Quotebox>

          <Quotebox
            onClick={() => handleOpen(2)}
            text={micro.getFromKey("heading.trip-protection", microcopy)}
            details={quoteBoxDetails.tripProtection}
            sx={{ display: { md: "none", lg: "block" } }}
          >
            <SvgIcon sx={{ fontSize: "18px" }}>
              <svg
                viewBox="0 0 18 18"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M11.25 1.5H4.5C4.10218 1.5 3.72064 1.65804 3.43934 1.93934C3.15804 2.22064 3 2.60218 3 3V15C3 15.3978 3.15804 15.7794 3.43934 16.0607C3.72064 16.342 4.10218 16.5 4.5 16.5H13.5C13.8978 16.5 14.2794 16.342 14.5607 16.0607C14.842 15.7794 15 15.3978 15 15V5.25L11.25 1.5Z"
                  stroke="#1878D0"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M10.5 1.5V4.5C10.5 4.89782 10.658 5.27936 10.9393 5.56066C11.2206 5.84196 11.6022 6 12 6H15"
                  stroke="#1878D0"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M7.5 6.75H6"
                  stroke="#1878D0"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M12 9.75H6"
                  stroke="#1878D0"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M12 12.75H6"
                  stroke="#1878D0"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </SvgIcon>
          </Quotebox>
          <Quotebox
            onClick={() => handleOpen(3)}
            text={micro.getFromKey("heading.number-people", microcopy)}
            details={quoteBoxDetails.numPeople}
            sx={{ display: { md: "none", lg: "block" } }}
          >
            <SvgIcon sx={{ fontSize: "18px" }}>
              <svg
                viewBox="0 0 18 18"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M13.5 15.75C13.5 14.1587 12.8679 12.6326 11.7426 11.5074C10.6174 10.3821 9.0913 9.75 7.5 9.75C5.9087 9.75 4.38258 10.3821 3.25736 11.5074C2.13214 12.6326 1.5 14.1587 1.5 15.75"
                  stroke="#1878D0"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M7.5 9.75C9.57107 9.75 11.25 8.07107 11.25 6C11.25 3.92893 9.57107 2.25 7.5 2.25C5.42893 2.25 3.75 3.92893 3.75 6C3.75 8.07107 5.42893 9.75 7.5 9.75Z"
                  stroke="#1878D0"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M16.5 15C16.5 12.4725 15 10.125 13.5 8.99999C13.993 8.63007 14.3873 8.14431 14.6479 7.5857C14.9085 7.02708 15.0274 6.41285 14.994 5.79734C14.9606 5.18184 14.776 4.58406 14.4566 4.05689C14.1371 3.52973 13.6926 3.08944 13.1625 2.77499"
                  stroke="#1878D0"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </SvgIcon>
          </Quotebox>
        </Grid>

        <Grid item>
          <Grid
            container
            sx={{
              gap: "8px",
              height: "92px",
              alignItems: "center",
              paddingInline: "30px",
              borderRadius: "20px",
              background:
                "radial-gradient(106.97% 648.3% at 48.22% 100%, #FFFFFF 0%, #E7EBF0 100%)",
            }}
          >
            <IconButton aria-label="edit" onClick={() => handleOpen(0)}>
              <SvgIcon>
                <svg
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <g clip-path="url(#clip0_61_157)">
                    <path
                      d="M17.645 5.67667C18.0856 5.23619 18.3332 4.63872 18.3332 4.01571C18.3333 3.3927 18.0859 2.79518 17.6454 2.35459C17.2049 1.91399 16.6075 1.66643 15.9845 1.66635C15.3614 1.66627 14.7639 1.91369 14.3233 2.35417L3.20166 13.4783C3.00818 13.6713 2.86509 13.9088 2.785 14.17L1.68416 17.7967C1.66263 17.8687 1.661 17.9453 1.67946 18.0182C1.69791 18.0911 1.73576 18.1577 1.78899 18.2108C1.84222 18.264 1.90885 18.3017 1.98179 18.3201C2.05474 18.3384 2.1313 18.3367 2.20333 18.315L5.83083 17.215C6.0918 17.1356 6.32931 16.9934 6.5225 16.8008L17.645 5.67667Z"
                      stroke="#212121"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M12.5 4.16667L15.8333 7.50001"
                      stroke="#212121"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_61_157">
                      <rect width="20" height="20" fill="white" />
                    </clipPath>
                  </defs>
                </svg>
              </SvgIcon>
            </IconButton>

            <SaveButton quoteRequest={formikProps.values} agentNum={agentNum} affiliate={affiliate} />

            <ResumeButton
              setQuoteData={setQuoteData}
              setQuoteBoxDetails={setQuoteBoxDetails}
              agentNum={agentNum}
            />

            <ResetButton setQuoteData={setQuoteData} />
          </Grid>
        </Grid>
      </Grid>

      <Modal
        open={appState.state.quoteModalOpen}
        onClose={handleClose}
        aria-labelledby="quote-modal-title"
        aria-describedby="quote-modal-description"
        sx={{ overflowY: "auto" }}
      >
        <Box>
          <QuoteModal
            onClose={handleClose}
            formikProps={formikProps}
            microcopySet={microcopy}
            rootTabDetails={quoteBoxDetails}
            updateBoxText={setQuoteBoxDetails}
            setDatesTabText={setEndDateTabText}
            setProtectionTabText={setProtectionTabText}
            setAreaTabText={setAreaTabText}
            setPeopleTabText={setPeopleTabText}
            startDateTabText={startDateTabText}
            endDateTabText={endDateTabText}
            areaTabText={areaTabText}
            protectionTabText={protectionTabText}
            peopleTabText={peopleTabText}
            helpIcons={helpIcons}
          />
        </Box>
      </Modal>

      <SpreadsheetImportModal
        formikProps={formikProps}
        setPeopleTabText={setPeopleTabText}
      />
    </>
  );
};

export default Quotebar;
